import React, { useState, useEffect, useCallback } from 'react';
import { Phone, PhoneOff, UserCheck, Users, Filter, Save, Play, Pause, ChevronDown, ChevronRight, Clock, CheckCircle, XCircle, Plus, Trash2, Download, RotateCcw, Search } from 'lucide-react';
import { getCurrentUser } from '../services/auth';
import { sessionHeaders } from '../services/auth';

/* ───── helpers ───── */
const NEON = (() => {
  const h = window.location.hostname;
  return (h === 'localhost' || h === '127.0.0.1')
    ? 'http://localhost:8888/.netlify/functions/neon'
    : '/.netlify/functions/neon';
})();

function neonQuery(query, params = {}) {
  return fetch(NEON, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...sessionHeaders() },
    body: JSON.stringify({ query, params }),
  }).then(r => r.json());
}

function fmt$(n) {
  if (n == null) return '—';
  return '$' + parseFloat(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateTime(d) {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    + ' ' + dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/* ───── constants ───── */
const CONTACT_OUTCOMES = [
  { value: '', label: '— Select outcome —' },
  { value: 'no_answer', label: "Didn't answer" },
  { value: 'voicemail', label: 'Left voicemail' },
  { value: 'callback_requested', label: 'Callback requested' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'hung_up', label: 'Hung up' },
  { value: 'wrong_number', label: 'Wrong number' },
  { value: 'do_not_call', label: 'Do not call' },
  { value: 'order_placed', label: 'Placed an order' },
  { value: 'upsell_added', label: 'Added upsell item' },
  { value: 'vip_signup', label: 'Signed up for VIP' },
  { value: 'email_signup', label: 'Email/SMS signup' },
  { value: 'other', label: 'Other (see notes)' },
];

const CONVERSION_OUTCOMES = new Set(['order_placed', 'upsell_added', 'vip_signup', 'email_signup']);

const SEGMENT_FILTERS = [
  { value: 'vip_unredeemed_reward', label: 'VIPs — Unredeemed VIP Cash (real)', description: 'Active VIPs who got a VIP-cash earn in a date window AND still hold a store-credit balance ≥ the smaller of (issued, minimum). Live Neon query.' },
  { value: 'vip_lapsed_since', label: 'VIPs — No Order Since Date (real)', description: 'Active VIPs whose last order is before the chosen date (or never ordered). Live Neon query.' },
  { value: 'recent_customers', label: 'Recent Customers (ordered last 90 days)', description: 'Customers with orders in the last 90 days — good for upsell/reorder' },
  { value: 'lapsed_customers', label: 'Lapsed Customers (no order 6+ months)', description: 'Haven\'t ordered in 6+ months — win-back campaign' },
  { value: 'high_value', label: 'High Value ($500+ lifetime)', description: 'Lifetime spend over $500 — VIP upgrade candidates' },
  { value: 'non_vip', label: 'Non-VIP Customers', description: 'Active customers not enrolled in VIP — enrollment push' },
  { value: 'cart_abandoners', label: 'Cart Abandoners (last 30 days)', description: 'Started checkout but didn\'t complete — close the sale' },
  { value: 'vip_expiring', label: 'VIP Expiring Soon (next 30 days)', description: 'VIP memberships expiring within 30 days — renewal outreach' },
  { value: 'no_email', label: 'No Email Opt-In', description: 'Customers without email marketing consent' },
  { value: 'pending_orders', label: 'Pending Orders (not shipped)', description: 'Orders placed but not yet shipped — upsell window' },
  { value: 'holiday_buyers', label: 'Holiday Buyers (Nov-Dec last year)', description: 'Purchased during last holiday season — seasonal re-engagement' },
  { value: 'custom', label: 'Custom Query...', description: 'Write your own segment criteria' },
];

// Segments that hit a real Neon query instead of the mock generator
const REAL_NEON_SEGMENTS = new Set(['vip_unredeemed_reward', 'vip_lapsed_since']);

const CHANNEL_OPTIONS = [
  { value: 'phone', label: 'Phone Call', icon: Phone },
  { value: 'email', label: 'Email', icon: null },
  { value: 'sms', label: 'Text/SMS', icon: null },
];

/* ───── localStorage persistence for prototype ───── */
const STORAGE_KEY = 'cpx_call_center';

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : { campaigns: [], contacts: [] };
  } catch { return { campaigns: [], contacts: [] }; }
}

function saveState(state) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

/* ───── mock data generator (until Neon queries are wired) ───── */
const FIRST = ['Sarah','Mike','Jennifer','David','Lisa','James','Emily','Robert','Karen','Chris','Amanda','Brian','Michelle','Kevin','Jessica','Matt','Nicole','Tom','Angela','Jeff','Stephanie','Mark','Heather','Jason','Rachel','Greg','Laura','Dan','Amy','Steve'];
const LAST = ['Johnson','Williams','Brown','Jones','Garcia','Miller','Davis','Rodriguez','Martinez','Wilson','Anderson','Taylor','Thomas','Jackson','White','Harris','Martin','Thompson','Moore','Clark','Lewis','Robinson','Walker','Young','Allen','King','Wright','Hill','Scott','Green'];
const DOMAINS = ['gmail.com','yahoo.com','outlook.com','aol.com','hotmail.com','comcast.net'];

function generateMockContacts(segmentType, count = 50) {
  const contacts = [];
  for (let i = 0; i < count; i++) {
    const first = FIRST[Math.floor(Math.random() * FIRST.length)];
    const last = LAST[Math.floor(Math.random() * LAST.length)];
    const domain = DOMAINS[Math.floor(Math.random() * DOMAINS.length)];
    const area = [410, 443, 301, 240, 202, 703, 804, 757, 215, 610][Math.floor(Math.random() * 10)];
    const daysAgo = Math.floor(Math.random() * 365);
    contacts.push({
      id: `cust_${Date.now()}_${i}`,
      customerId: 10000 + Math.floor(Math.random() * 90000),
      firstName: first,
      lastName: last,
      email: `${first.toLowerCase()}.${last.toLowerCase()}@${domain}`,
      phone: `(${area}) ${String(Math.floor(Math.random() * 900) + 100)}-${String(Math.floor(Math.random() * 9000) + 1000)}`,
      lastOrderDate: new Date(Date.now() - daysAgo * 86400000).toISOString(),
      totalOrders: Math.floor(Math.random() * 20) + 1,
      lifetimeSpend: Math.floor(Math.random() * 2000) + 50,
      isVip: Math.random() > 0.7,
      lastContactDate: Math.random() > 0.6 ? new Date(Date.now() - Math.floor(Math.random() * 60) * 86400000).toISOString() : null,
    });
  }
  return contacts;
}

/* ───── sub-components ───── */

function KpiCard({ label, value, sub, color }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '14px 18px' }}>
      <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: color || 'var(--navy)', marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function OutcomeBadge({ outcome }) {
  const colors = {
    no_answer: { bg: '#f3f4f6', color: '#6b7280' },
    voicemail: { bg: '#fef3c7', color: '#92400e' },
    callback_requested: { bg: '#dbeafe', color: '#1e40af' },
    not_interested: { bg: '#fecaca', color: '#991b1b' },
    hung_up: { bg: '#fecaca', color: '#991b1b' },
    wrong_number: { bg: '#f3f4f6', color: '#6b7280' },
    do_not_call: { bg: '#1f2937', color: '#fff' },
    order_placed: { bg: '#d1fae5', color: '#065f46' },
    upsell_added: { bg: '#d1fae5', color: '#065f46' },
    vip_signup: { bg: '#ede9fe', color: '#5b21b6' },
    email_signup: { bg: '#fef3c7', color: '#92400e' },
    other: { bg: '#f3f4f6', color: '#6b7280' },
  };
  const s = colors[outcome] || colors.other;
  const label = CONTACT_OUTCOMES.find(o => o.value === outcome)?.label || outcome;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: s.bg, color: s.color, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

/* ───── main component ───── */
export default function CallCenterOutreach({ navigateTo }) {
  const user = getCurrentUser();
  const [view, setView] = useState('campaigns'); // campaigns | builder | queue | history
  const [state, setState] = useState(loadState);
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [queue, setQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [contactNote, setContactNote] = useState('');
  const [contactOutcome, setContactOutcome] = useState('');
  const [loading, setLoading] = useState(false);

  // Builder state
  const [campaignName, setCampaignName] = useState('');
  const [segmentType, setSegmentType] = useState('vip_unredeemed_reward');
  const [channel, setChannel] = useState('phone');
  const [cooldownDays, setCooldownDays] = useState(14);
  const [maxContacts, setMaxContacts] = useState(50);
  const [customQuery, setCustomQuery] = useState('');
  const [assignees, setAssignees] = useState([]);
  const [previewContacts, setPreviewContacts] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  // Date-window params for the two real-Neon segments
  const [sinceDate, setSinceDate] = useState('2026-04-01');
  const [rewardStartDate, setRewardStartDate] = useState('2026-03-25');
  const [rewardEndDate, setRewardEndDate] = useState('2026-04-07');
  const [rewardMinAmount, setRewardMinAmount] = useState(25);
  const [previewError, setPreviewError] = useState(null);

  // persist
  useEffect(() => { saveState(state); }, [state]);

  /* ── Campaign Builder ── */
  // Shape a Neon row into the contact object the rest of the page consumes.
  // Source rows come from call_center_vip_lapsed_since / call_center_vip_unredeemed_reward.
  const shapeNeonContact = (r) => ({
    id: `cust_${r.customer_id || r.bc_customer_id}_${Math.random().toString(36).slice(2, 7)}`,
    customerId: r.bc_customer_id || r.customer_id,
    firstName: (r.first_name || '').trim(),
    lastName: (r.last_name || '').trim(),
    email: r.email || '',
    phone: r.phone || '',
    lastOrderDate: r.last_order_date,
    totalOrders: Number(r.order_count || 0),
    lifetimeSpend: Number(r.lifetime_value || 0),
    isVip: true,
    // vm.auto_renew is already selected by the VIP segment queries.
    vipNotRenewing: r.auto_renew === false,
    lastContactDate: null,
    storeCredit: Number(r.current_store_credit || 0),
    rewardAt: r.reward_at || null,
    rewardAmount: r.reward_amount != null ? Number(r.reward_amount) : null,
  });

  const handlePreview = useCallback(async () => {
    setLoading(true);
    setPreviewError(null);

    // Path A — real Neon query for the two production segments.
    if (REAL_NEON_SEGMENTS.has(segmentType)) {
      try {
        let query, params;
        if (segmentType === 'vip_lapsed_since') {
          query = 'call_center_vip_lapsed_since';
          params = { since_date: sinceDate, limit: maxContacts, offset: 0 };
        } else {
          query = 'call_center_vip_unredeemed_reward';
          params = {
            start_date: rewardStartDate,
            end_date: rewardEndDate,
            min_amount: Number(rewardMinAmount) || 0,
            limit: maxContacts,
            offset: 0,
          };
        }
        const resp = await neonQuery(query, params);
        if (resp.error) throw new Error(resp.error);
        const rows = resp.rows || [];
        setPreviewContacts(rows.map(shapeNeonContact));
        setShowPreview(true);
      } catch (err) {
        setPreviewError(err.message || 'Neon query failed');
        setPreviewContacts([]);
        setShowPreview(true);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Path B — mock data for the prototype-only segments. Cooldown filter
    // is meaningless on a fresh mock set but keep the existing UX.
    setTimeout(() => {
      const contacts = generateMockContacts(segmentType, maxContacts);
      const filtered = contacts.filter(c => {
        if (!c.lastContactDate) return true;
        const daysSince = (Date.now() - new Date(c.lastContactDate).getTime()) / 86400000;
        return daysSince >= cooldownDays;
      });
      setPreviewContacts(filtered);
      setShowPreview(true);
      setLoading(false);
    }, 600);
  }, [segmentType, maxContacts, cooldownDays, sinceDate, rewardStartDate, rewardEndDate, rewardMinAmount]);

  const handleSaveCampaign = () => {
    if (!campaignName.trim()) return alert('Give this campaign a name');
    const campaign = {
      id: `camp_${Date.now()}`,
      name: campaignName.trim(),
      segmentType,
      channel,
      cooldownDays,
      maxContacts,
      customQuery: segmentType === 'custom' ? customQuery : null,
      createdBy: user?.staffName || 'Unknown',
      createdAt: new Date().toISOString(),
      status: 'draft', // draft | active | paused | completed
      assignees: assignees.length ? assignees : [user?.staffName || 'Unknown'],
      contacts: previewContacts.map(c => ({
        ...c,
        status: 'pending', // pending | in_progress | completed | skipped
        outcome: null,
        notes: '',
        contactedAt: null,
        contactedBy: null,
      })),
      stats: { total: previewContacts.length, contacted: 0, conversions: 0, revenue: 0 },
    };
    setState(prev => ({ ...prev, campaigns: [...prev.campaigns, campaign] }));
    setCampaignName('');
    setPreviewContacts([]);
    setShowPreview(false);
    setView('campaigns');
  };

  /* ── Queue Management ── */
  const startCampaign = (campaign) => {
    const myName = user?.staffName || 'Unknown';
    // Filter to contacts assigned to this user (or all if they're the only assignee)
    const myContacts = campaign.contacts.filter(c => c.status === 'pending' || c.status === 'in_progress');
    if (!myContacts.length) return alert('No pending contacts in this campaign');

    // Mark campaign active
    setState(prev => ({
      ...prev,
      campaigns: prev.campaigns.map(c =>
        c.id === campaign.id ? { ...c, status: 'active' } : c
      ),
    }));
    setActiveCampaign(campaign);
    setQueue(myContacts);
    setCurrentIndex(0);
    setContactNote('');
    setContactOutcome('');
    setView('queue');
  };

  const handleLogContact = () => {
    if (!contactOutcome) return alert('Select an outcome');
    const contact = queue[currentIndex];
    const myName = user?.staffName || 'Unknown';
    const isConversion = CONVERSION_OUTCOMES.has(contactOutcome);

    // Update campaign contacts
    setState(prev => ({
      ...prev,
      campaigns: prev.campaigns.map(camp => {
        if (camp.id !== activeCampaign.id) return camp;
        const updatedContacts = camp.contacts.map(c => {
          if (c.id !== contact.id) return c;
          return { ...c, status: 'completed', outcome: contactOutcome, notes: contactNote, contactedAt: new Date().toISOString(), contactedBy: myName };
        });
        const completed = updatedContacts.filter(c => c.status === 'completed').length;
        const conversions = updatedContacts.filter(c => CONVERSION_OUTCOMES.has(c.outcome)).length;
        return {
          ...camp,
          contacts: updatedContacts,
          stats: { ...camp.stats, contacted: completed, conversions },
        };
      }),
      contacts: [...prev.contacts, {
        customerId: contact.customerId,
        customerName: `${contact.firstName} ${contact.lastName}`,
        outcome: contactOutcome,
        notes: contactNote,
        channel,
        contactedBy: myName,
        contactedAt: new Date().toISOString(),
        campaignId: activeCampaign.id,
        campaignName: activeCampaign.name,
      }],
    }));

    // Advance to next
    setContactNote('');
    setContactOutcome('');
    if (currentIndex < queue.length - 1) {
      setCurrentIndex(i => i + 1);
    } else {
      // Campaign queue finished
      setState(prev => ({
        ...prev,
        campaigns: prev.campaigns.map(c =>
          c.id === activeCampaign.id ? { ...c, status: 'completed' } : c
        ),
      }));
      setView('campaigns');
      setActiveCampaign(null);
    }
  };

  const skipContact = () => {
    const contact = queue[currentIndex];
    setState(prev => ({
      ...prev,
      campaigns: prev.campaigns.map(camp => {
        if (camp.id !== activeCampaign.id) return camp;
        return {
          ...camp,
          contacts: camp.contacts.map(c =>
            c.id === contact.id ? { ...c, status: 'skipped' } : c
          ),
        };
      }),
    }));
    if (currentIndex < queue.length - 1) {
      setCurrentIndex(i => i + 1);
    } else {
      setView('campaigns');
    }
  };

  /* ── Delete campaign ── */
  const deleteCampaign = (id) => {
    setState(prev => ({ ...prev, campaigns: prev.campaigns.filter(c => c.id !== id) }));
  };

  /* ── Export results ── */
  const exportResults = (campaign) => {
    const rows = [['Customer ID', 'Name', 'Email', 'Phone', 'Outcome', 'Notes', 'Contacted By', 'Contacted At']];
    campaign.contacts.filter(c => c.status === 'completed').forEach(c => {
      rows.push([c.customerId, `${c.firstName} ${c.lastName}`, c.email, c.phone, c.outcome, c.notes, c.contactedBy, c.contactedAt]);
    });
    const csv = rows.map(r => r.map(v => `"${String(v || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${campaign.name.replace(/\s+/g, '_')}_results.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  /* ── renders ── */
  const campaigns = state.campaigns;
  const totalContacted = state.contacts.length;
  const totalConversions = state.contacts.filter(c => CONVERSION_OUTCOMES.has(c.outcome)).length;
  const activeCampaigns = campaigns.filter(c => c.status === 'active' || c.status === 'draft').length;

  return (
    <div style={{ padding: 24, maxWidth: 1200 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, color: 'var(--navy)' }}>Call Center Outreach</h2>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Logged in as <strong>{user?.staffName || 'Unknown'}</strong></div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {['campaigns', 'builder', 'history'].map(v => (
            <button key={v} onClick={() => setView(v)}
              style={{
                padding: '7px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                border: view === v ? '2px solid var(--navy)' : '1px solid #d1d5db',
                background: view === v ? 'var(--navy)' : '#fff',
                color: view === v ? '#fff' : '#374151',
              }}>
              {v === 'campaigns' ? 'My Campaigns' : v === 'builder' ? '+ New Campaign' : 'Contact History'}
            </button>
          ))}
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        <KpiCard label="Active Campaigns" value={activeCampaigns} />
        <KpiCard label="Total Contacted" value={totalContacted} />
        <KpiCard label="Conversions" value={totalConversions} color="#059669" />
        <KpiCard label="Conversion Rate" value={totalContacted ? `${((totalConversions / totalContacted) * 100).toFixed(1)}%` : '—'} color="var(--orange)" />
      </div>

      {/* ════════ CAMPAIGNS LIST ════════ */}
      {view === 'campaigns' && (
        <div>
          {campaigns.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
              <Users size={48} style={{ marginBottom: 12, opacity: 0.4 }} />
              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>No campaigns yet</div>
              <div style={{ fontSize: 13 }}>Create your first outreach campaign to get started</div>
              <button onClick={() => setView('builder')} style={{ marginTop: 16, padding: '8px 20px', background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
                + New Campaign
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {campaigns.slice().reverse().map(camp => {
                const pct = camp.stats.total ? Math.round((camp.stats.contacted / camp.stats.total) * 100) : 0;
                const convRate = camp.stats.contacted ? ((camp.stats.conversions / camp.stats.contacted) * 100).toFixed(1) : '0';
                const statusColors = { draft: '#6b7280', active: '#059669', paused: '#d97706', completed: '#1e40af' };
                return (
                  <div key={camp.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)' }}>{camp.name}</span>
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: statusColors[camp.status] + '20', color: statusColors[camp.status], textTransform: 'uppercase' }}>
                            {camp.status}
                          </span>
                          <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 8, background: '#f3f4f6', color: '#6b7280' }}>
                            {SEGMENT_FILTERS.find(f => f.value === camp.segmentType)?.label || camp.segmentType}
                          </span>
                        </div>
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                          Created by {camp.createdBy} on {fmtDate(camp.createdAt)} &middot; {camp.channel} &middot; {camp.assignees.join(', ')}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {(camp.status === 'draft' || camp.status === 'active' || camp.status === 'paused') && (
                          <button onClick={() => startCampaign(camp)} style={{ padding: '6px 14px', background: '#059669', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Play size={12} /> {camp.status === 'draft' ? 'Start' : 'Resume'}
                          </button>
                        )}
                        {camp.status === 'completed' && (
                          <button onClick={() => exportResults(camp)} style={{ padding: '6px 14px', background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Download size={12} /> Export
                          </button>
                        )}
                        <button onClick={() => deleteCampaign(camp.id)} style={{ padding: '6px 10px', background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div style={{ marginTop: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#6b7280', marginBottom: 4 }}>
                        <span>{camp.stats.contacted} / {camp.stats.total} contacted ({pct}%)</span>
                        <span>{camp.stats.conversions} conversions ({convRate}%)</span>
                      </div>
                      <div style={{ height: 6, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: pct === 100 ? '#1e40af' : '#059669', borderRadius: 3, transition: 'width 0.3s' }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ════════ CAMPAIGN BUILDER ════════ */}
      {view === 'builder' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {/* Left: Config */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 15, color: 'var(--navy)' }}>Build Segment</h3>

            <label style={lbl}>Campaign Name</label>
            <input value={campaignName} onChange={e => setCampaignName(e.target.value)}
              placeholder="e.g. March VIP Push" style={inp} />

            <label style={lbl}>Segment Type</label>
            <select value={segmentType} onChange={e => setSegmentType(e.target.value)} style={inp}>
              {SEGMENT_FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: -8, marginBottom: 12 }}>
              {SEGMENT_FILTERS.find(f => f.value === segmentType)?.description}
            </div>

            {segmentType === 'custom' && (
              <>
                <label style={lbl}>Custom Query Description</label>
                <textarea value={customQuery} onChange={e => setCustomQuery(e.target.value)}
                  placeholder="Describe the segment criteria, e.g. 'Customers in MD who ordered crabs in the last 60 days but are not VIP members'"
                  rows={3} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />
              </>
            )}

            {segmentType === 'vip_lapsed_since' && (
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: 12, marginBottom: 12 }}>
                <label style={lbl}>No order since</label>
                <input type="date" value={sinceDate} onChange={e => setSinceDate(e.target.value)} style={inp} />
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: -8 }}>
                  Active VIPs whose most recent order date is before this. Defaults to 2026-04-01 (the VIP cash mailing).
                </div>
              </div>
            )}

            {segmentType === 'vip_unredeemed_reward' && (
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: 12, marginBottom: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div>
                    <label style={lbl}>Reward issued from</label>
                    <input type="date" value={rewardStartDate} onChange={e => setRewardStartDate(e.target.value)} style={inp} />
                  </div>
                  <div>
                    <label style={lbl}>Reward issued through</label>
                    <input type="date" value={rewardEndDate} onChange={e => setRewardEndDate(e.target.value)} style={inp} />
                  </div>
                </div>
                <label style={lbl}>Min current store credit ($)</label>
                <input type="number" value={rewardMinAmount} onChange={e => setRewardMinAmount(e.target.value)}
                  min={0} step={1} style={inp} />
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: -8 }}>
                  Customers must currently hold ≥ min(issued amount, this value) in store credit.
                  Set to ~$25 to catch the $30 mailing even if the customer spent a few dollars.
                </div>
              </div>
            )}

            <label style={lbl}>Channel</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              {CHANNEL_OPTIONS.map(ch => (
                <button key={ch.value} onClick={() => setChannel(ch.value)}
                  style={{
                    flex: 1, padding: '8px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    border: channel === ch.value ? '2px solid var(--navy)' : '1px solid #d1d5db',
                    background: channel === ch.value ? '#eef2ff' : '#fff',
                    color: channel === ch.value ? 'var(--navy)' : '#6b7280',
                  }}>
                  {ch.label}
                </button>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div>
                <label style={lbl}>Cooldown Period (days)</label>
                <input type="number" value={cooldownDays} onChange={e => setCooldownDays(Number(e.target.value))}
                  min={0} max={365} style={inp} />
                <div style={{ fontSize: 10, color: '#9ca3af', marginTop: -8 }}>Skip customers contacted within this period</div>
              </div>
              <div>
                <label style={lbl}>Max Contacts</label>
                <input type="number" value={maxContacts} onChange={e => setMaxContacts(Number(e.target.value))}
                  min={1} max={500} style={inp} />
              </div>
            </div>

            <label style={lbl}>Assign To (comma-separated names)</label>
            <input value={assignees.join(', ')} onChange={e => setAssignees(e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
              placeholder={user?.staffName || 'Leave blank for yourself'} style={inp} />

            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handlePreview} disabled={loading}
                style={{ flex: 1, padding: '10px 16px', background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 13, opacity: loading ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <Search size={14} /> {loading ? 'Loading...' : 'Preview Segment'}
              </button>
              {showPreview && previewContacts.length > 0 && (
                <button onClick={handleSaveCampaign}
                  style={{ flex: 1, padding: '10px 16px', background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <Save size={14} /> Save Campaign ({previewContacts.length} contacts)
                </button>
              )}
            </div>
          </div>

          {/* Right: Preview */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20, maxHeight: 600, overflow: 'auto' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 15, color: 'var(--navy)' }}>Segment Preview</h3>
            {previewError && (
              <div style={{ background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 6, padding: 10, marginBottom: 12, fontSize: 12 }}>
                <strong>Query failed:</strong> {previewError}
              </div>
            )}
            {!showPreview ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
                <Filter size={36} style={{ marginBottom: 8, opacity: 0.4 }} />
                <div style={{ fontSize: 13 }}>Configure your segment and click Preview</div>
              </div>
            ) : previewContacts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
                <XCircle size={36} style={{ marginBottom: 8, opacity: 0.4 }} />
                <div style={{ fontSize: 13 }}>No contacts match these criteria</div>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 12, color: '#059669', fontWeight: 600, marginBottom: 8 }}>
                  {previewContacts.length} contacts found
                  {REAL_NEON_SEGMENTS.has(segmentType) && <span style={{ color: '#6b7280', fontWeight: 400, marginLeft: 6 }}>(live Neon)</span>}
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                      <th style={th}>Name</th>
                      <th style={th}>Phone</th>
                      <th style={th}>Orders</th>
                      <th style={th}>Spend</th>
                      {segmentType === 'vip_unredeemed_reward' && <th style={th}>Reward</th>}
                      {REAL_NEON_SEGMENTS.has(segmentType) && <th style={th}>VIP Cash</th>}
                      <th style={th}>Last Order</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewContacts.slice(0, 25).map(c => (
                      <tr key={c.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={td}>
                          <div>{c.firstName} {c.lastName}</div>
                          {c.email && <div style={{ fontSize: 10, color: '#6b7280', marginTop: 1 }}>{c.email}</div>}
                        </td>
                        <td style={td}>{c.phone}</td>
                        <td style={{ ...td, textAlign: 'center' }}>{c.totalOrders}</td>
                        <td style={td}>{fmt$(c.lifetimeSpend)}</td>
                        {segmentType === 'vip_unredeemed_reward' && (
                          <td style={td}>
                            {fmt$(c.rewardAmount)}
                            {c.rewardAt && <div style={{ fontSize: 10, color: '#9ca3af' }}>{fmtDate(c.rewardAt)}</div>}
                          </td>
                        )}
                        {REAL_NEON_SEGMENTS.has(segmentType) && (
                          <td style={{ ...td, fontWeight: 600, color: c.storeCredit >= 25 ? '#059669' : '#6b7280' }}>
                            {fmt$(c.storeCredit)}
                          </td>
                        )}
                        <td style={td}>{c.lastOrderDate ? fmtDate(c.lastOrderDate) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {previewContacts.length > 25 && (
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>Showing 25 of {previewContacts.length} contacts</div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ════════ CONTACT QUEUE ════════ */}
      {view === 'queue' && activeCampaign && queue.length > 0 && (
        <div>
          {/* Queue header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 13, color: '#6b7280' }}>
              <strong>{activeCampaign.name}</strong> &middot; Contact {currentIndex + 1} of {queue.length}
            </div>
            <button onClick={() => { setView('campaigns'); setActiveCampaign(null); }}
              style={{ padding: '6px 14px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Pause size={12} /> Pause & Return
            </button>
          </div>

          {/* Progress */}
          <div style={{ height: 4, background: '#f3f4f6', borderRadius: 2, marginBottom: 20, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${((currentIndex) / queue.length) * 100}%`, background: '#059669', borderRadius: 2, transition: 'width 0.3s' }} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            {/* Contact Card */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--navy)' }}>
                    {queue[currentIndex].firstName} {queue[currentIndex].lastName}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Customer #{queue[currentIndex].customerId}</div>
                </div>
                {queue[currentIndex].isVip && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 10, background: '#ede9fe', color: '#5b21b6', whiteSpace: 'nowrap' }}>{queue[currentIndex].vipNotRenewing ? 'VIP MEMBER - NOT RENEWING' : 'VIP MEMBER'}</span>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Phone</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--navy)', marginTop: 2 }}>
                    <a href={`tel:${queue[currentIndex].phone.replace(/\D/g, '')}`} style={{ color: 'var(--navy)', textDecoration: 'none' }}>
                      {queue[currentIndex].phone}
                    </a>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Email</div>
                  <div style={{ fontSize: 13, color: '#374151', marginTop: 2, wordBreak: 'break-all' }}>{queue[currentIndex].email}</div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
                <div style={{ background: '#f9fafb', borderRadius: 6, padding: 10, textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600 }}>ORDERS</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--navy)' }}>{queue[currentIndex].totalOrders}</div>
                </div>
                <div style={{ background: '#f9fafb', borderRadius: 6, padding: 10, textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600 }}>LIFETIME</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--navy)' }}>{fmt$(queue[currentIndex].lifetimeSpend)}</div>
                </div>
                <div style={{ background: '#f9fafb', borderRadius: 6, padding: 10, textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600 }}>LAST ORDER</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)' }}>{fmtDate(queue[currentIndex].lastOrderDate)}</div>
                </div>
              </div>

              {queue[currentIndex].lastContactDate && (
                <div style={{ fontSize: 11, color: '#d97706', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Clock size={12} /> Last contacted: {fmtDate(queue[currentIndex].lastContactDate)}
                </div>
              )}

              {navigateTo && (
                <button onClick={() => navigateTo('customer-search', null, queue[currentIndex].email)}
                  style={{ marginTop: 12, padding: '6px 12px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', color: '#374151' }}>
                  View Full Customer Record →
                </button>
              )}
            </div>

            {/* Log Contact */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 24 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, color: 'var(--navy)' }}>Log Contact</h3>

              <label style={lbl}>Outcome</label>
              <select value={contactOutcome} onChange={e => setContactOutcome(e.target.value)} style={{ ...inp, fontSize: 14 }}>
                {CONTACT_OUTCOMES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>

              <label style={lbl}>Notes</label>
              <textarea value={contactNote} onChange={e => setContactNote(e.target.value)}
                placeholder="Any details about the conversation..."
                rows={4} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />

              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button onClick={handleLogContact}
                  style={{ flex: 2, padding: '12px 16px', background: '#059669', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <CheckCircle size={16} /> Log & Next
                </button>
                <button onClick={skipContact}
                  style={{ flex: 1, padding: '12px 16px', background: '#f3f4f6', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
                  Skip
                </button>
              </div>

              {/* Quick outcome buttons */}
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>Quick Select</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {CONTACT_OUTCOMES.filter(o => o.value).map(o => (
                    <button key={o.value} onClick={() => setContactOutcome(o.value)}
                      style={{
                        padding: '4px 10px', borderRadius: 12, fontSize: 10, fontWeight: 600, cursor: 'pointer',
                        border: contactOutcome === o.value ? '2px solid var(--navy)' : '1px solid #e5e7eb',
                        background: contactOutcome === o.value ? '#eef2ff' : '#fff',
                        color: CONVERSION_OUTCOMES.has(o.value) ? '#059669' : '#6b7280',
                      }}>
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ════════ CONTACT HISTORY ════════ */}
      {view === 'history' && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 15, color: 'var(--navy)' }}>Contact History</h3>
            <div style={{ fontSize: 12, color: '#6b7280' }}>{state.contacts.length} total contacts logged</div>
          </div>
          {state.contacts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: 13 }}>No contacts logged yet</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={th}>Date</th>
                  <th style={th}>Customer</th>
                  <th style={th}>Campaign</th>
                  <th style={th}>Channel</th>
                  <th style={th}>Outcome</th>
                  <th style={th}>Agent</th>
                  <th style={th}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {state.contacts.slice().reverse().map((c, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={td}>{fmtDateTime(c.contactedAt)}</td>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{c.customerName}</div>
                      <div style={{ fontSize: 10, color: '#9ca3af' }}>#{c.customerId}</div>
                    </td>
                    <td style={td}>{c.campaignName}</td>
                    <td style={td}>{c.channel}</td>
                    <td style={td}><OutcomeBadge outcome={c.outcome} /></td>
                    <td style={td}>{c.contactedBy}</td>
                    <td style={{ ...td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

/* ───── shared styles ───── */
const lbl = { display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.3px' };
const inp = { display: 'block', width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, marginBottom: 12, boxSizing: 'border-box', outline: 'none' };
const th = { textAlign: 'left', padding: '6px 8px', fontWeight: 600, color: '#6b7280', fontSize: 11, textTransform: 'uppercase' };
const td = { padding: '8px 8px', color: '#374151' };
